// shared/vmsDuty/civilDate.ts
/**
 * Calendar arithmetic on 'YYYY-MM-DD' strings. NOTHING here constructs a `Date`.
 *
 * 🔴 Why: parsing '2026-09-21' through the Date constructor gives UTC midnight, which is 05:30
 * IST, so any later local-time read shifts the day. A roster week is a calendar fact, not an
 * instant, so it is integers.
 *
 * ⚠️ This comment is deliberately worded without the constructor call spelled out: the
 * source-pin test in __tests__/duties.test.ts greps these files for it, and a comment quoting
 * the forbidden form turns the guard red. Do not loosen the regex to make prose fit.
 *
 * Howard Hinnant's days-from-civil / civil-from-days (public domain), proleptic Gregorian,
 * day 0 = 1970-01-01. `Math.floor` everywhere: JS `/` is float and `%` is remainder.
 */
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface CivilDate { y: number; m: number; d: number }

export function daysFromCivil(y: number, m: number, d: number): number {
  const yy = y - (m <= 2 ? 1 : 0);
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;                                          // [0, 399]
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

export function civilFromDays(dayCount: number): CivilDate {
  const z = dayCount + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;                                        // [0, 146096]
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp + (mp < 10 ? 3 : -9);
  return { y: y + (m <= 2 ? 1 : 0), m, d };
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

export function toIso(c: CivilDate): string {
  return `${pad(c.y, 4)}-${pad(c.m, 2)}-${pad(c.d, 2)}`;
}

/**
 * True only for a well-formed AND real calendar date. The round-trip is what rejects
 * 2026-02-30 and 2026-04-31 — a regex accepts both, and an off-by-one bug that starts from an
 * accepted impossible date is invisible.
 */
export function isIsoDate(value: string): boolean {
  const m = ISO_RE.exec(value);
  if (!m) return false;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  return toIso(civilFromDays(daysFromCivil(y, mo, d))) === value;
}

export function parseIso(value: string): CivilDate {
  const m = ISO_RE.exec(value);
  if (!m || !isIsoDate(value)) throw new RangeError(`Not a real YYYY-MM-DD date: ${JSON.stringify(value)}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function dayNumber(dateIso: string): number {
  const c = parseIso(dateIso);
  return daysFromCivil(c.y, c.m, c.d);
}

export function addDays(dateIso: string, n: number): string {
  if (!Number.isInteger(n)) throw new RangeError(`addDays needs a whole number of days; got ${n}`);
  return toIso(civilFromDays(dayNumber(dateIso) + n));
}

/** `toDateIso` − `fromIso`, in whole days. Negative when `toDateIso` is earlier. */
export function diffDays(fromIso: string, toDateIso: string): number {
  return dayNumber(toDateIso) - dayNumber(fromIso);
}

/** 0 = Monday … 6 = Sunday. Day 0 (1970-01-01) was a Thursday, hence the +3. */
export function weekdayIndex(dateIso: string): number {
  return (((dayNumber(dateIso) + 3) % 7) + 7) % 7;
}

export function mondayOf(dateIso: string): string {
  return addDays(dateIso, -weekdayIndex(dateIso));
}
